/**
 * Merges array B into array A based on _id comparison
 * If an element with the same _id exists in A, it will be overwritten by the element from B
 * If an element from B doesn't exist in A, it will be added to A
 * 
 * @param {Array} arrayA - The base array to merge into
 * @param {Array} arrayB - The array to merge from
 * @returns {Array} - The merged array
 */
export const mergeArrayById = (arrayA, arrayB) => {
  // Create a copy of array A to avoid mutating the original
  const result = [...arrayA];
  
  // Create a Map for quick lookup by _id
  const mapA = new Map(result.map((item, index) => [item._id, index]));
  
  // Iterate through array B
  arrayB.forEach(itemB => {
    const existingIndex = mapA.get(itemB._id);
    
    if (existingIndex !== undefined) {
      // Element exists in A, overwrite it
      result[existingIndex] = itemB;
    } else {
      // Element doesn't exist in A, add it
      result.push(itemB);
      mapA.set(itemB._id, result.length - 1);
    }
  });
  
  return result;
};

/**
 * Alternative version that merges the 'images' properties instead of completely replacing
 * This preserves images from A that aren't in B
 * 
 * @param {Array} arrayA - The base array to merge into
 * @param {Array} arrayB - The array to merge from
 * @returns {Array} - The merged array with deep merge of 'images' properties
 */
export const mergeArrayByIdDeep = (arrayA, arrayB) => {
  // Create a copy of array A to avoid mutating the original
  const result = [...arrayA];
  
  // Create a Map for quick lookup by _id
  const mapA = new Map(result.map((item, index) => [item._id, index]));
  
  // Iterate through array B
  arrayB.forEach(itemB => {
    const existingIndex = mapA.get(itemB._id);
    
    if (existingIndex !== undefined) {
      // Element exists in A, merge the images properties
      result[existingIndex] = {
        ...result[existingIndex],
        ...itemB,
        images: {
          ...result[existingIndex].images,
          ...itemB.images
        }
      };
    } else {
      // Element doesn't exist in A, add it
      result.push(itemB);
      mapA.set(itemB._id, result.length - 1);
    }
  });
  
  return result;
};